import { DATE_FORMAT_RFC } from 'common/constants'
import { loadNs } from 'common/i18n-config'
import { Asset } from 'common/models'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { BulkChangeEvent, rowSelectSliceAssetList } from 'pages/assets/AssetListPage/store'

import { useGetSelectedItemOnPage } from '../AssetList/components/AssetListTopTemplate/hooks'
import { getSmallAmountAssetCostLimitErrorKey, ValueFields } from './helpers'

const t = loadNs(['components/organisms/assets/asset-form', 'common'])

/**
 * Checks the assets a start-usage drawer covers against the small-amount depreciable asset
 * per-unit ceiling that the currently picked usage-started date implies.
 *
 * The asset detail drawers hand over the single asset on screen; bulk start usage passes none
 * and is checked against the rows selected on the list instead.
 *
 * Returns the message to show under the date picker, or undefined when everything fits.
 * Gated behind the `FaD80AcquisitionCostValidation` flag, so it is inert while that is off.
 */
export const useSmallAmountAssetCostLimitError = (asset?: Asset) => {
  const { values } = useFormikContext<ValueFields>()
  const { selectedItems } = useGetSelectedItemOnPage()
  const bulkChangeEvent = rowSelectSliceAssetList.useSlice(state => state?.data?.bulkChangeEvent)
  const { isEnable: isAcquisitionCostValidationEnabled } = useCheckFlag(FeatureFlagNames.FaD80AcquisitionCostValidation)

  // The picker hands back a moment until formik normalises it, so read both shapes.
  const usageStartedAt = values?.usage_started_at ? moment(values.usage_started_at).format(DATE_FORMAT_RFC) : undefined
  const bulkAssets = bulkChangeEvent === BulkChangeEvent.START_USAGE ? selectedItems : []
  const assetsToCheck = asset ? [asset] : bulkAssets
  const costLimitErrorKey = isAcquisitionCostValidationEnabled
    ? getSmallAmountAssetCostLimitErrorKey(assetsToCheck, usageStartedAt)
    : undefined

  return costLimitErrorKey ? t(costLimitErrorKey) : undefined
}
